/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { omit, get } from 'lodash';
import { Icon, Message } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { prepareFloatForServer } from '../../../../../lib/numberFormatter';
import { Modal, Button } from '../../../../../components';
import ServiceForm from './ServiceForm';
import { GET_SERVICE_GROUPS_QUERY } from '../../../containers/service/ServicesList';

type AddServiceDialogProps = {
    intl: Object,
    trigger?: any,
    open: boolean,
    onClose: () => void,
    serviceGroup: { id: string },
};

const intlMessages = defineMessages({
    nameExistingsError: {
        id: 'pages.settings.services.add.nameExistingsError',
        defaultMessage: 'Service with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const CREATE_SERVICE_MUTATION = gql`
    mutation createService($input: CreateServiceInput!, $lang: String!) {
        createService(input: $input) {
            service {
                id
                name(lang: $lang)
                nameVariants {
                    lv
                    ru
                    en
                }
                duration
                price
                color
            }
        }
    }
`;

const AddServiceDialog = ({
    trigger,
    open,
    onClose,
    serviceGroup,
    intl,
}: AddServiceDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl: intlState }) =>
            intlState.get('locale') || intlState.get('defaultLanguage'),
    );
    const languages = useSelector(({ intl: intlState }) =>
        intlState.get('languages'),
    );

    const buildDefaultValues = () => ({
        nameVariants: (languages || []).reduce(
            (acc, lang) => ({ ...acc, [lang]: '' }),
            {},
        ),
        duration: null,
        price: '',
        color: '',
    });

    const methods = useForm({
        defaultValues: buildDefaultValues(),
    });

    const { reset, setError, handleSubmit, formState: { isSubmitting, isDirty, errors } } = methods;

    useEffect(() => {
        if (open) {
            reset(buildDefaultValues());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reset]);

    const [createService] = useMutation(CREATE_SERVICE_MUTATION);

    const onSubmit = async formData => {
        try {
            const duration = formData.duration.toString();
            const price = prepareFloatForServer(formData.price);
            await createService({
                variables: {
                    input: {
                        serviceGroupId: serviceGroup.id,
                        duration,
                        price,
                        ...omit(formData, ['duration', 'price']),
                    },
                    lang: currentLanguage,
                },
                update: (store, { data: { createService: result } }) => {
                    const data = store.readQuery({
                        query: GET_SERVICE_GROUPS_QUERY,
                        variables: {
                            salon,
                            lang: currentLanguage,
                        },
                    });

                    if (
                        data &&
                        data.viewer &&
                        data.viewer.salon &&
                        data.viewer.salon.serviceGroups &&
                        data.viewer.salon.serviceGroups.nodes
                    ) {
                        const newNodes = data.viewer.salon.serviceGroups.nodes.map(
                            group => {
                                if (group.id === serviceGroup.id) {
                                    return {
                                        ...group,
                                        services: [
                                            ...(group.services || []),
                                            result.service,
                                        ],
                                    };
                                }
                                return group;
                            },
                        );

                        store.writeQuery({
                            query: GET_SERVICE_GROUPS_QUERY,
                            variables: {
                                salon,
                                lang: currentLanguage,
                            },
                            data: {
                                ...data,
                                viewer: {
                                    ...data.viewer,
                                    salon: {
                                        ...data.viewer.salon,
                                        serviceGroups: {
                                            ...data.viewer.salon.serviceGroups,
                                            nodes: newNodes,
                                        },
                                    },
                                },
                            },
                        });
                    }
                },
            });
            onClose();
        } catch (error) {
            if (error.graphQLErrors && error.graphQLErrors.length) {
                const graphQLError = error.graphQLErrors[0];
                if (
                    graphQLError.name === 'AlreadyExists' &&
                    get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.nameExistingsError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal size="small" trigger={trigger} open={open} onClose={onClose}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.settings.services.add.addService"
                        defaultMessage="Add Service"
                    />
                </Modal.Header>
                <Modal.Content>
                    {errors.root && (
                        <Message error>
                            <Icon name="warning sign" />
                            {errors.root.message}
                        </Message>
                    )}
                    <ServiceForm onSubmit={onSubmit} />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        onClick={handleSubmit(onSubmit)}
                        primary
                    >
                        <Icon name="checkmark" />
                        <FormattedMessage
                            id="pages.settings.services.add.add"
                            defaultMessage="Add"
                        />
                    </Button>
                    <Button disabled={isSubmitting} onClick={onClose} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.settings.services.add.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
};

export default injectIntl(AddServiceDialog);
